import { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { EmailChip, ChipErrorReason } from "./types";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface EmailChipInputProps {
  chips: EmailChip[];
  onChipsChange: (chips: EmailChip[]) => void;
  ownerEmail: string;
  existingEmails: string[];
  memberEmails: string[];
}

let chipIdCounter = 0;

function validateEmail(
  email: string,
  ownerEmail: string,
  memberEmails: string[],
): { status: "valid" | "invalid"; errorReason?: ChipErrorReason } {
  if (!EMAIL_REGEX.test(email)) {
    return { status: "invalid", errorReason: "invalidFormat" };
  }
  if (email.toLowerCase() === ownerEmail.toLowerCase()) {
    return { status: "invalid", errorReason: "ownerEmail" };
  }
  if (!memberEmails.some((m) => m.toLowerCase() === email.toLowerCase())) {
    return { status: "invalid", errorReason: "unauthorized" };
  }
  return { status: "valid" };
}

export function EmailChipInput({
  chips,
  onChipsChange,
  ownerEmail,
  existingEmails,
  memberEmails,
}: EmailChipInputProps) {
  const { t } = useTranslation("sharing");
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [popoverContainer, setPopoverContainer] = useState<HTMLElement | null>(
    null,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let node: HTMLElement | null = inputRef.current;
    while (node && node.getAttribute("role") !== "dialog") {
      node = node.parentElement;
    }
    setPopoverContainer(node);
  }, []);

  const suggestions: string[] = inputValue.trim()
    ? memberEmails.filter((m) => {
        const email = m.toLowerCase();
        const q = inputValue.trim().toLowerCase();
        return (
          email.includes(q) &&
          email !== ownerEmail.toLowerCase() &&
          !existingEmails.some((e) => e.toLowerCase() === email) &&
          !chips.some((c) => c.email.toLowerCase() === email)
        );
      })
    : [];

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [suggestions.length]);

  const addChips = (rawEmails: string[]) => {
    const next = [...chips];
    let changed = false;
    for (const raw of rawEmails) {
      const email = raw.trim().toLowerCase();
      if (!email) continue;
      if (next.some((c) => c.email === email)) continue;
      const validation = validateEmail(email, ownerEmail, memberEmails);
      next.push({
        id: `chip-${++chipIdCounter}`,
        email,
        ...validation,
      });
      changed = true;
    }
    if (changed) {
      onChipsChange(next);
    }
    setInputValue("");
    setShowSuggestions(false);
  };

  const removeChip = (id: string) => {
    onChipsChange(chips.filter((c) => c.id !== id));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Tab" || e.key === ",") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        addChips([suggestions[highlightedIndex]]);
      } else if (inputValue.trim()) {
        addChips([inputValue]);
      }
      return;
    }

    if (e.key === "Backspace" && !inputValue && chips.length > 0) {
      removeChip(chips[chips.length - 1].id);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0,
      );
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1,
      );
      return;
    }

    if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.includes(",")) {
      addChips(value.split(","));
      return;
    }
    setInputValue(value);
    setShowSuggestions(value.trim().length > 0);
  };

  const handleSuggestionClick = (email: string) => {
    addChips([email]);
    inputRef.current?.focus();
  };

  const getChipErrorMessage = (reason?: ChipErrorReason): string => {
    switch (reason) {
      case "unauthorized":
        return t("chip.error.unauthorized");
      case "invalidFormat":
        return t("chip.error.invalidFormat");
      case "ownerEmail":
        return t("chip.error.ownerEmail");
      default:
        return "";
    }
  };

  const isOpen = showSuggestions && suggestions.length > 0;

  return (
    <Popover open={isOpen} onOpenChange={setShowSuggestions}>
      <PopoverAnchor asChild>
        <div
          className="flex flex-wrap items-center gap-1.5 rounded-xl border border-secondary-300 bg-background px-3 py-2 text-sm ring-offset-background focus-within:shadow-base min-h-[2.5rem] cursor-text"
          onClick={() => inputRef.current?.focus()}
        >
          {chips.map((chip) => {
            const isInvalid = chip.status === "invalid";
            const chipContent = (
              <Badge
                key={chip.id}
                className={`gap-1 px-2 py-0.5 text-xs font-normal w-auto rounded-md ${
                  isInvalid
                    ? "bg-rose-50 text-destructive border border-destructive/30"
                    : "bg-secondary-50 text-foreground border border-secondary-200"
                }`}
              >
                {chip.email}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeChip(chip.id);
                  }}
                  className="ml-0.5 rounded-full hover:bg-black/10 p-0.5 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );

            if (isInvalid) {
              return (
                <TooltipProvider key={chip.id} delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>{chipContent}</TooltipTrigger>
                    <TooltipContent>
                      <p>{getChipErrorMessage(chip.errorReason)}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            }

            return chipContent;
          })}

          <input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (inputValue.trim()) setShowSuggestions(true);
            }}
            placeholder={chips.length === 0 ? t("modal.emailPlaceholder") : ""}
            className="flex-1 min-w-[8rem] bg-transparent outline-none placeholder:text-muted-foreground text-sm"
            type="text"
            autoComplete="off"
          />
        </div>
      </PopoverAnchor>

      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={4}
        container={popoverContainer ?? undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="w-[var(--radix-popover-trigger-width)] max-h-[18rem] overflow-y-auto rounded-xl border bg-popover p-1 shadow-md"
      >
        {suggestions.map((email, index) => (
          <button
            key={email}
            type="button"
            className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
              index === highlightedIndex
                ? "bg-accent text-accent-foreground"
                : "hover:bg-accent hover:text-accent-foreground"
            }`}
            onClick={() => handleSuggestionClick(email)}
            onMouseEnter={() => setHighlightedIndex(index)}
          >
            <span className="text-foreground">{email}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
